import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Center,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Stack,
  Text,
  useToast,
  Alert,
  AlertIcon,
  Image,
  Flex,
  Spinner,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { presentationAuth } from '../../firebase/config';
import { signInWithEmailAndPassword, onAuthStateChanged } from 'firebase/auth';

const AdminLoginPage: React.FC = () => {
  // Methodist brand colors
  const methodistBlue = '#0066CC';
  const methodistNavy = '#002D72';
  
  // State
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState<boolean>(true);
  
  const navigate = useNavigate();
  const toast = useToast();
  
  // Check if already authenticated
  useEffect(() => {
    console.log('[ADMIN AUTH] Checking authentication status...');
    setIsCheckingAuth(true);
    
    const unsubscribe = onAuthStateChanged(presentationAuth, (user) => {
      if (user) {
        console.log('[ADMIN AUTH] User already authenticated, redirecting to admin');
        // Short delay to prevent flickering
        setTimeout(() => {
          navigate('/admin');
        }, 100);
      } else {
        console.log('[ADMIN AUTH] No authenticated user found');
        setIsCheckingAuth(false);
      }
    });
    
    return () => unsubscribe();
  }, [navigate]);
  
  // Handle login
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setAuthError(null);
    
    try {
      console.log('[ADMIN AUTH] Attempting login with:', email);
      // Use the presentation auth instance from config.ts
      const userCredential = await signInWithEmailAndPassword(presentationAuth, email, password);
      console.log('[ADMIN AUTH] Login successful');
      
      // Store user info in localStorage for additional persistence
      localStorage.setItem('admin_email', email);
      localStorage.setItem('admin_authenticated', 'true');
      
      // Navigate directly to admin page
      navigate('/admin');
      
      toast({
        title: 'Login successful',
        description: 'Welcome to the administration portal.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      console.error('[ADMIN AUTH] Login error:', error);
      setAuthError('Invalid email or password. Please try again.');
      
      toast({
        title: 'Login failed',
        description: 'Invalid email or password. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Show loading spinner while checking authentication
  if (isCheckingAuth) {
    return (
      <Center minH="100vh" bg="#f8f9fa">
        <Spinner 
          size="xl" 
          color={methodistBlue} 
          thickness="4px"
          speed="0.65s"
        />
      </Center>
    );
  }
  
  return (
    <Box minH="100vh" bg="#f8f9fa">
      <Center minH="100vh">
        <Box 
          w="100%" 
          maxW="400px" 
          p={8} 
          borderWidth="1px" 
          borderRadius="lg" 
          boxShadow="lg"
          bg="white"
        >
          <Stack spacing={6} align="center" mb={8}>
            <Image src="/assets/houston-methodist-official-logo.png" alt="Houston Methodist" height="40px" />
            <Heading size="lg" color={methodistNavy}>Administration Login</Heading>
          </Stack>
          
          {authError && (
            <Alert status="error" mb={6} borderRadius="md">
              <AlertIcon />
              {authError}
            </Alert>
          )}
          
          <form onSubmit={handleLogin}>
            <Stack spacing={4}>
              <FormControl id="email" isRequired>
                <FormLabel>Email</FormLabel>
                <Input 
                  type="email" 
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@example.com"
                />
              </FormControl>
              
              <FormControl id="password" isRequired>
                <FormLabel>Password</FormLabel>
                <Input 
                  type="password" 
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="********"
                />
              </FormControl>
              
              <Button
                type="submit"
                colorScheme="blue"
                bg={methodistBlue}
                size="lg"
                fontSize="md"
                isLoading={isLoading}
                loadingText="Signing in..."
                width="100%"
                mt={4}
              >
                Sign In
              </Button>
            </Stack>
          </form>
        </Box>
      </Center>
    </Box>
  );
};

export default AdminLoginPage;
